import React from "react";
import * as api from "../services/api";
import { useLoaderData, useRevalidator } from "react-router";
import { formData, FormErrorList, useFormAction } from "../form";
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListSubheader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ButtonLoader from "../components/ButtonLoader";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { updateIn } from "shared/src/util.mjs";

/**
 * @typedef WebhookConfig
 * @property {number} relid
 * @property {boolean} on_insert
 * @property {string[]} on_update_columns
 * @property {boolean} on_delete
 * @property {string} schema
 * @property {string} table
 * @property {string[]} available_columns
 */

export async function loader() {
  return api.loadWebhookConfigData();
}

function formConverter(formData) {
  return updateIn(formData, ["on_update_columns"], (columns) =>
    Object.entries(columns).reduce((acc, [k, v]) => {
      if (v) {
        acc.push(k);
      }

      return acc;
    }, []),
  );
}

/**
 * @param {Object} props
 * @param {WebhookConfig[]} props.webhookConfigs
 */
function EditWebhookConfigDialog({ onClose, webhookConfig }) {
  const { t } = useTranslation();
  const revalidator = useRevalidator();
  const { loading, errors, onSubmit } = useFormAction(
    (e) => api.patchWebhookConfigData(formConverter(formData(e.currentTarget))),
    () => {
      revalidator.revalidate();
      onClose();
    },
  );
  const on_update_columns = new Set(webhookConfig.on_update_columns);

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>{webhookConfig.table}</DialogTitle>
      <form onSubmit={onSubmit}>
        <DialogContent>
          <input type="hidden" name="relid" value={webhookConfig.relid} />
          <FormControlLabel
            name="on_insert"
            control={<Checkbox defaultChecked={webhookConfig.on_insert} />}
            label="Vložení záznamu"
          />
          <FormControlLabel
            name="on_delete"
            control={<Checkbox defaultChecked={webhookConfig.on_delete} />}
            label="Smazání záznamu"
          />
          <List subheader={<ListSubheader>Aktualizace sloupce</ListSubheader>}>
            {webhookConfig.available_columns.map((c) => (
              <ListItem key={c} disablePadding>
                <FormControlLabel
                  name={`on_update_columns.${c}`}
                  control={
                    <Checkbox defaultChecked={on_update_columns.has(c)} />
                  }
                  label={c}
                />
              </ListItem>
            ))}
          </List>
          <FormErrorList formErrors={errors} />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("form.cancel")}</Button>
          <Button type="submit" disabled={loading}>
            {loading && <ButtonLoader />}
            {t("form.submit")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/**
 * @param {Object} props
 * @param {WebhookConfig[]} props.webhookConfigs
 */
function PageTable({ webhookConfigs }) {
  const [editWebhookConfig, setEditWebhookConfig] = React.useState();

  return (
    <>
      <Card>
        <CardContent>
          <Typography sx={{ mb: 2 }} variant="h2">
            Struktura webhooku
          </Typography>

          <Typography variant="h3">id</Typography>
          <p>Unikátní identifikátor zprávy.</p>

          <Typography variant="h3">created_at</Typography>
          <p>Čas vytvoření zprávy.</p>

          <Typography variant="h3">payload</Typography>
          <p>
            Obsah zprávy. Vždy obsahuje property <i>operation</i>, od které se
            odvíjí další properties a jejich význam.
          </p>

          <ul>
            <li>
              INSERT
              <ul>
                <li>entity - tabulka, do které byl vložen záznam</li>
                <li>id - id vloženého záznamu</li>
              </ul>
            </li>
            <li>
              UPDATE
              <ul>
                <li>entity - tabulka, ve které byl upraven záznam</li>
                <li>id - id upraveného záznamu</li>
              </ul>
            </li>
            <li>
              DELETE
              <ul>
                <li>entity - tabulka, ze které byl smazán záznam</li>
                <li>id - id smazaného záznamu</li>
              </ul>
            </li>
            <li>
              export-request
              <ul>
                <li>
                  client_id - id klienta, u kterého vznikl požadavek na export
                </li>
                <li>
                  files - soubory, u kterých vznikl požadavek na export pro
                  klinta (soubor se identifikuje pomocí 3 hodnot: <i>type</i>,{" "}
                  <i>objectid</i>, <i>file</i>)
                </li>
              </ul>
            </li>
            <li>
              export-medical-report-request
              <ul>
                <li>
                  client_id - id klienta, u kterého vznikl požadavek na export
                </li>
              </ul>
            </li>
          </ul>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tabulka</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {webhookConfigs.map((wc) => (
                <TableRow key={wc.relid}>
                  <TableCell>{wc.table}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => setEditWebhookConfig(wc)}>
                      <FontAwesomeIcon icon={faPen} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      {editWebhookConfig && (
        <EditWebhookConfigDialog
          onClose={() => setEditWebhookConfig(null)}
          webhookConfig={editWebhookConfig}
        />
      )}
    </>
  );
}

export default function WebhookConfig() {
  const loaderData = useLoaderData();
  const webhookConfigs = loaderData.data.webhookConfigs.data;

  return <PageTable webhookConfigs={webhookConfigs}></PageTable>;
}
